/**
 * ESG Page — Templates + Submissions
 *
 * Two sections:
 *   - Templates: table with name, type, version, fund
 *   - Submissions: table with investee, fund, period, status, completion %
 *   - Status workflow buttons: Submit, Validate, Approve
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Leaf,
  FileText,
  ClipboardCheck,
  CheckCircle,
  Send,
  Plus,
  X,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { apiClient } from '@/api/client';
import { StatusBadge, CompletionBar } from '@/components/ui/StatusBadge';
import type { ESGTemplate, ESGSubmission, Fund, Investee } from '@/types';

type TabKey = 'templates' | 'submissions';

const STATUS_VARIANTS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'danger'> = {
  draft: 'default',
  submitted: 'info',
  validating: 'info',
  validated: 'warning',
  approved: 'success',
  rejected: 'danger',
};

const STATUS_ACTIONS: Record<string, { label: string; action: string; icon: typeof Send; isDanger?: boolean }[]> = {
  draft: [{ label: 'Submit', action: 'submit', icon: Send }],
  submitted: [
    { label: 'Validate', action: 'validate', icon: CheckCircle },
  ],
  validating: [
    { label: 'Validate', action: 'validate', icon: CheckCircle },
  ],
  validated: [
    { label: 'Approve', action: 'approve', icon: CheckCircle },
  ],
  approved: [],
  rejected: [{ label: 'Submit', action: 'submit', icon: Send }],
};

export function ESGPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('submissions');
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showCreateSubmission, setShowCreateSubmission] = useState(false);

  const templatesQuery = useQuery<ESGTemplate[]>({
    queryKey: ['ic', 'esg', 'templates'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/esg/templates/');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 60_000,
    enabled: activeTab === 'templates',
  });

  const submissionsQuery = useQuery<ESGSubmission[]>({
    queryKey: ['ic', 'esg', 'submissions'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/esg/submissions/');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 30_000,
    enabled: activeTab === 'submissions',
  });

  const fundsQuery = useQuery<{ id: number; name: string }[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const investeesQuery = useQuery<{ id: number; name: string }[]>({
    queryKey: ['ic', 'investees'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/investees/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const funds = fundsQuery.data ?? [];
  const investees = investeesQuery.data ?? [];

  const statusMutation = useMutation({
    mutationFn: async ({ id, action }: { id: number; action: string }) => {
      const res = await apiClient.post(`/api/v1/impact-core/esg/submissions/${id}/${action}`);
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ic', 'esg', 'submissions'] });
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-ic-text-primary">ESG Data Management</h1>
        <p className="text-xs text-ic-text-tertiary">Templates and data submissions</p>
      </div>

      {/* Tab Switcher */}
      <div className="border-b border-ic-border">
        <div className="flex gap-0">
          <TabButton
            label="Submissions"
            icon={<ClipboardCheck className="h-3.5 w-3.5" />}
            active={activeTab === 'submissions'}
            onClick={() => setActiveTab('submissions')}
          />
          <TabButton
            label="Templates"
            icon={<FileText className="h-3.5 w-3.5" />}
            active={activeTab === 'templates'}
            onClick={() => setActiveTab('templates')}
          />
        </div>
      </div>

      {/* Content */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
        {/* Create Template Button */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowCreateTemplate(true)}
            className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Create Template
          </button>
        </div>

        <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
          {templatesQuery.isLoading ? (
            <LoadingSkeleton />
          ) : templatesQuery.isError ? (
            <ErrorState message="Failed to load templates." />
          ) : (templatesQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon={<FileText className="h-5 w-5" />}
              message="No ESG templates configured yet."
              action={
                <button
                  onClick={() => setShowCreateTemplate(true)}
                  className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Create Template
                </button>
              }
            />
          ) : (
            <table className="w-full text-xs ic-table">
              <thead>
                <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                  <th className="px-4 pb-2 pt-3 font-medium">Name</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Type</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Version</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Fund</th>
                  <th className="px-4 pb-2 pt-3 font-medium text-right">Indicators</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(templatesQuery.data ?? []).map((tmpl) => (
                  <tr key={tmpl.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                    <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{tmpl.name}</td>
                    <td className="py-2.5 px-4">
                      <StatusBadge label={tmpl.template_type} variant="info" />
                    </td>
                    <td className="py-2.5 px-4 font-mono text-xs text-ic-text-secondary">{tmpl.version ?? '1.0'}</td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{tmpl.fund_name ?? 'Global'}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-xs text-ic-text-secondary">
                      {tmpl.indicator_count ?? '--'}
                    </td>
                    <td className="py-2.5 px-4">
                      <StatusBadge label={tmpl.status ?? 'active'} variant="success" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create Template Modal */}
        {showCreateTemplate && (
          <TemplateCreateModal
            onClose={() => setShowCreateTemplate(false)}
            onCreated={() => {
              setShowCreateTemplate(false);
              void queryClient.invalidateQueries({ queryKey: ['ic', 'esg', 'templates'] });
            }}
          />
        )}
        </div>
      )}

      {activeTab === 'submissions' && (
        <div className="space-y-4">
        {/* Create Submission Button */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowCreateSubmission(true)}
            className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Create Submission
          </button>
        </div>

        <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
          {submissionsQuery.isLoading ? (
            <LoadingSkeleton />
          ) : submissionsQuery.isError ? (
            <ErrorState message="Failed to load submissions." />
          ) : (submissionsQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon={<Leaf className="h-5 w-5" />}
              message="No ESG submissions yet."
              action={
                <button
                  onClick={() => setShowCreateSubmission(true)}
                  className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Create Submission
                </button>
              }
            />
          ) : (
            <table className="w-full text-xs ic-table">
              <thead>
                <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                  <th className="px-4 pb-2 pt-3 font-medium">Investee</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Fund</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Period</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Status</th>
                  <th className="px-4 pb-2 pt-3 font-medium w-32">Completion</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Submitted</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(submissionsQuery.data ?? []).map((sub) => {
                  const actions = STATUS_ACTIONS[sub.status] ?? [];
                  const investeeName = sub.investee_name ?? investees.find((inv) => inv.id === sub.investee_id)?.name ?? 'Investee (removed)';
                  const fundName = sub.fund_name ?? funds.find((f) => f.id === sub.fund_id)?.name ?? (sub.fund_id ? 'Fund (removed)' : '--');
                  return (
                    <tr key={sub.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                      <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{investeeName}</td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{fundName}</td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{sub.reporting_period ?? '--'}</td>
                      <td className="py-2.5 px-4">
                        <StatusBadge label={sub.status} variant={STATUS_VARIANTS[sub.status] ?? 'default'} />
                      </td>
                      <td className="py-2.5 px-4">
                        <CompletionBar percent={sub.completion_percent ?? 0} />
                      </td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-tertiary">
                        {sub.submitted_at ? (() => { try { return format(parseISO(sub.submitted_at), 'dd MMM yyyy'); } catch { return sub.submitted_at; } })() : '--'}
                      </td>
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-1.5">
                          {actions.map((action) => (
                            <button
                              key={action.action}
                              onClick={() => statusMutation.mutate({ id: sub.id, action: action.action })}
                              disabled={statusMutation.isPending}
                              className={`flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium transition-colors disabled:opacity-50 ${
                                action.isDanger
                                  ? 'text-red-400 hover:bg-red-500/10'
                                  : 'text-ic-accent hover:bg-ic-accent/10'
                              }`}
                            >
                              <action.icon className="h-3 w-3" />
                              {action.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Create Submission Modal */}
        {showCreateSubmission && (
          <SubmissionCreateModal
            funds={funds as Fund[]}
            investees={investees as Investee[]}
            templates={(templatesQuery.data ?? []) as ESGTemplate[]}
            onClose={() => setShowCreateSubmission(false)}
            onCreated={() => {
              setShowCreateSubmission(false);
              void queryClient.invalidateQueries({ queryKey: ['ic', 'esg', 'submissions'] });
            }}
          />
        )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────

function TabButton({ label, icon, active, onClick }: { label: string; icon: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
        active
          ? 'border-ic-accent text-ic-accent'
          : 'border-transparent text-ic-text-tertiary hover:text-ic-text-secondary'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function LoadingSkeleton() {
  return (
    <div className="p-4 space-y-2">
      {[1, 2, 3, 4].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />)}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex h-48 items-center justify-center">
      <p className="text-xs text-red-400">{message}</p>
    </div>
  );
}

function EmptyState({ icon, message, action }: { icon: React.ReactNode; message: string; action?: React.ReactNode }) {
  return (
    <div className="flex h-48 flex-col items-center justify-center gap-3">
      <span className="text-ic-text-tertiary">{icon}</span>
      <p className="text-xs text-ic-text-tertiary">{message}</p>
      {action}
    </div>
  );
}

// ─── Template Create Modal ────────────────────────────────────

const TEMPLATE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'esg', label: 'ESG' },
  { value: 'financial', label: 'Financial' },
  { value: 'combined', label: 'Combined' },
];

function TemplateCreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [templateType, setTemplateType] = useState('esg');
  const [description, setDescription] = useState('');
  const [indicators, setIndicators] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: async (payload: { name: string; template_type: string; description?: string; indicators?: string[] }) => {
      const res = await apiClient.post('/api/v1/impact-core/esg/templates/', payload);
      return res.data;
    },
    onSuccess: () => {
      onCreated();
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string | Array<{ msg: string; loc: string[] }> } } };
      const detail = axiosErr.response?.data?.detail;
      if (typeof detail === 'string') {
        setErrors({ _general: detail });
      } else if (Array.isArray(detail)) {
        const mapped: Record<string, string> = {};
        for (const item of detail) {
          const field = item.loc[item.loc.length - 1] ?? '_general';
          mapped[field] = item.msg;
        }
        setErrors(mapped);
      } else {
        setErrors({ _general: 'Failed to create template. Please try again.' });
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (!name.trim()) {
      setErrors({ name: 'Template name is required' });
      return;
    }

    const indicatorList = indicators
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    createMutation.mutate({
      name: name.trim(),
      template_type: templateType,
      description: description.trim() || undefined,
      indicators: indicatorList.length > 0 ? indicatorList : undefined,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">Create ESG Template</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}

          <TemplateFieldGroup label="Template Name *" error={errors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. SFDR PAI Template 2026"
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
            />
          </TemplateFieldGroup>

          <TemplateFieldGroup label="Template Type" error={errors.template_type}>
            <select
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value)}
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
            >
              {TEMPLATE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </TemplateFieldGroup>

          <TemplateFieldGroup label="Description" error={errors.description}>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Brief description of this template..."
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none resize-none"
            />
          </TemplateFieldGroup>

          <TemplateFieldGroup label="Indicators (one per line)" error={errors.indicators}>
            <textarea
              value={indicators}
              onChange={(e) => setIndicators(e.target.value)}
              rows={4}
              placeholder={"GHG Emissions (Scope 1)\nGHG Emissions (Scope 2)\nCarbon Footprint\nBoard Gender Diversity"}
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none resize-none font-mono"
            />
          </TemplateFieldGroup>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplateFieldGroup({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
        {label}
      </label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

// ─── Submission Create Modal ─────────────────────────────────

function SubmissionCreateModal({
  funds,
  investees,
  templates,
  onClose,
  onCreated,
}: {
  funds: Fund[];
  investees: Investee[];
  templates: ESGTemplate[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [fundId, setFundId] = useState('');
  const [investeeId, setInvesteeId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [reportingPeriod, setReportingPeriod] = useState('');
  const [reportingYear, setReportingYear] = useState(new Date().getFullYear().toString());
  const [reportingQuarter, setReportingQuarter] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMsg, setSuccessMsg] = useState('');

  // Fetch templates if not already loaded (they load on templates tab only)
  const templatesQuery = useQuery<ESGTemplate[]>({
    queryKey: ['ic', 'esg', 'templates'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/esg/templates/');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 60_000,
    initialData: templates.length > 0 ? templates : undefined,
  });

  const allTemplates = templatesQuery.data ?? templates;

  const createMutation = useMutation({
    mutationFn: async (payload: {
      investee_id: number;
      fund_id: number;
      reporting_period: string;
      reporting_year: number;
      template_id?: number;
      reporting_quarter?: string;
    }) => {
      const res = await apiClient.post('/api/v1/impact-core/esg/submissions/', payload);
      return res.data;
    },
    onSuccess: () => {
      setSuccessMsg('Submission created successfully.');
      setTimeout(() => onCreated(), 800);
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string | Array<{ msg: string; loc: string[] }> } } };
      const detail = axiosErr.response?.data?.detail;
      if (typeof detail === 'string') {
        setErrors({ _general: detail });
      } else if (Array.isArray(detail)) {
        const mapped: Record<string, string> = {};
        for (const item of detail) {
          const field = item.loc[item.loc.length - 1] ?? '_general';
          mapped[field] = item.msg;
        }
        setErrors(mapped);
      } else {
        setErrors({ _general: 'Failed to create submission. Please try again.' });
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});
    setSuccessMsg('');

    if (!fundId) { setErrors({ fund_id: 'Select a fund' }); return; }
    if (!investeeId) { setErrors({ investee_id: 'Select an investee' }); return; }
    if (!reportingPeriod.trim()) { setErrors({ reporting_period: 'Reporting period is required' }); return; }
    if (!reportingYear || isNaN(Number(reportingYear))) { setErrors({ reporting_year: 'Valid year is required' }); return; }

    createMutation.mutate({
      investee_id: Number(investeeId),
      fund_id: Number(fundId),
      reporting_period: reportingPeriod.trim(),
      reporting_year: Number(reportingYear),
      template_id: templateId ? Number(templateId) : undefined,
      reporting_quarter: reportingQuarter || undefined,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">Create ESG Submission</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}
          {successMsg && (
            <div className="rounded-md bg-emerald-500/10 border border-emerald-500/20 px-3 py-2 text-xs text-emerald-400">
              {successMsg}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <SubmissionFieldGroup label="Fund *" error={errors.fund_id}>
              <select
                value={fundId}
                onChange={(e) => setFundId(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">Select fund...</option>
                {funds.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </SubmissionFieldGroup>

            <SubmissionFieldGroup label="Investee *" error={errors.investee_id}>
              <select
                value={investeeId}
                onChange={(e) => setInvesteeId(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">Select investee...</option>
                {investees.map((inv) => (
                  <option key={inv.id} value={inv.id}>{inv.name}</option>
                ))}
              </select>
            </SubmissionFieldGroup>
          </div>

          <SubmissionFieldGroup label="Template (optional)" error={errors.template_id}>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
            >
              <option value="">No template</option>
              {allTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </SubmissionFieldGroup>

          <div className="grid grid-cols-3 gap-4">
            <SubmissionFieldGroup label="Reporting Period *" error={errors.reporting_period}>
              <input
                type="text"
                value={reportingPeriod}
                onChange={(e) => setReportingPeriod(e.target.value)}
                placeholder="e.g. 2026-Q1"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
              />
            </SubmissionFieldGroup>

            <SubmissionFieldGroup label="Reporting Year *" error={errors.reporting_year}>
              <input
                type="number"
                value={reportingYear}
                onChange={(e) => setReportingYear(e.target.value)}
                min="2020"
                max="2100"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none"
              />
            </SubmissionFieldGroup>

            <SubmissionFieldGroup label="Quarter (optional)">
              <select
                value={reportingQuarter}
                onChange={(e) => setReportingQuarter(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">--</option>
                <option value="Q1">Q1</option>
                <option value="Q2">Q2</option>
                <option value="Q3">Q3</option>
                <option value="Q4">Q4</option>
              </select>
            </SubmissionFieldGroup>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Submission'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SubmissionFieldGroup({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
        {label}
      </label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}
